import { Box, Fade, Stack, Typography, ButtonBase, Popper } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { createContext, useContext, useEffect, useRef, useState } from "react"

import {
  Button,
  InquiryModal,
  TripModal,
  Link,
  Logo,
  SvgIcon,
  type FC,
  type FCWC,
  type TripData,
} from ".."
import { SiteMetadataContext } from "../../local/wrappers/SiteMetadataProvider"
import { useTripsData } from "../../local/hooks"

type SubnavLinkProps = {
  title: string
  uri: string
  active?: boolean
}

type GeoPageLink = {
  title: string
  uri: string
}

type DynamicSubNavProps = {
  hideRight?: boolean
  color?: "white" | "yellow"
  links: SubnavLinkProps[]
  trip?: TripData
  travelInformation?: GeoPageLink[] | null
  tripIdea?: GeoPageLink[] | null
  countryId?: number | null
  activeLink?: string | null
}

const DynamicSubNav: FC<DynamicSubNavProps> = ({
  links = [],
  color = "white",
  hideRight = false,
  trip = null,
  travelInformation = null,
  tripIdea = null,
  countryId = null,
  activeLink,
}) => {
  const order = ["Trip Itineraries", "Places To Go", "Travel Guides"]
  const sortedLinks = [...links].sort((a, b) => {
    return order.indexOf(a.title) - order.indexOf(b.title)
  })

  return (
    <SubNavContainer color={color}>
      <SubNavLogo />
      <SubNavDesktopLinks
        links={sortedLinks}
        travelInformation={travelInformation}
        tripIdea={tripIdea}
        isTripPage={!!trip}
        countryId={countryId}
        activeLink={activeLink}
      />
      <SubNavRightContent hideRight={hideRight} trip={trip as TripData} />
      <SubNavMobileLinks
        links={sortedLinks}
        travelInformation={travelInformation}
        tripIdea={tripIdea}
        isTripPage={!!trip}
        countryId={countryId}
        activeLink={activeLink}
      />
    </SubNavContainer>
  )
}
export default DynamicSubNav

/** Create a context to detect with the navigation is sticky */
const SubNavStickyContext = createContext(false)

/** A sub-component for the container of the dynamic sub navigation component */
const SubNavContainer: FCWC<Pick<DynamicSubNavProps, "color">> = ({ color, children }) => {
  const ref = useRef<HTMLDivElement>(null)
  const [isSticky, setIsSticky] = useState(false)

  useEffect(() => {
    if (ref.current) {
      const cachedRef = ref.current
      const observer = new IntersectionObserver(
        ([e]) => setIsSticky(e.boundingClientRect.top < 0),
        {
          threshold: [1],
        }
      )
      observer.observe(cachedRef)
      return () => observer.unobserve(cachedRef)
    }
  }, [ref])

  return (
    <Box
      ref={ref}
      component="nav"
      position="sticky"
      top={-1}
      borderTop={(theme) =>
        `1px solid ${theme.palette[color === "white" ? "neutral.100" : "tertiary.200"].main}`
      }
      zIndex={1100}
    >
      <Box
        px={2.5}
        py={isSticky ? 2 : 0}
        mb={{ xs: 0, md: 2 }}
        position="relative"
        sx={(theme) => ({
          bgcolor: isSticky
            ? "transparent"
            : theme.palette[color === "white" ? "neutral.100" : "tertiary.200"].main,
          transition: (theme) => theme.transitions.create("all"),
          overflow: isSticky ? "hidden" : "unset",
          [theme.breakpoints.down("md")]: {
            borderBottom: `1px solid ${
              isSticky
                ? "transparent"
                : color === "white"
                ? theme.palette["neutral.200"].main
                : "transparent"
            }`,
            ...(!isSticky && {
              "overflowY": "scroll",
              "scrollbarWidth": "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }),
          },
        })}
      >
        <Stack
          bgcolor={(theme) =>
            isSticky
              ? theme.palette["neutral.100"].main
              : theme.palette[color === "white" ? "neutral.100" : "tertiary.200"].main
          }
          borderRadius={isSticky ? 1.5 : 0}
          boxShadow={(theme) =>
            isSticky ? `0px 0px 10px 0px ${alpha(theme.palette["neutral.900"].main, 0.2)}` : "none"
          }
          direction="row"
          alignItems="center"
          gap={{ xs: 0, md: 15 }}
          sx={(theme) => ({
            transition: (theme) => theme.transitions.create("all"),
            [theme.breakpoints.up("md")]: {
              borderBottom: `1px solid ${
                isSticky
                  ? "transparent"
                  : color === "white"
                  ? theme.palette["neutral.200"].main
                  : "transparent"
              }`,
            },

            [theme.breakpoints.down(1400)]: {
              flexWrap: "wrap",
            },
          })}
        >
          <SubNavStickyContext.Provider value={isSticky}>{children}</SubNavStickyContext.Provider>
        </Stack>
      </Box>
    </Box>
  )
}

/** A sub-component for the logo in the dynamic sub navigation component */
const SubNavLogo: FC = () => {
  const isSticky = useContext(SubNavStickyContext)

  return (
    <Fade in={isSticky} unmountOnExit>
      <Stack
        justifyContent="center"
        flex="1"
        ml={{ xs: 3.5, md: 8 }}
        height={{ xs: 54, md: 74 }}
        width={114}
      >
        <Logo size={{ height: { xs: 22, md: 30 }, width: { xs: 84, md: 114 } }} />
      </Stack>
    </Fade>
  )
}

/** A sub-component for the individual link in the dynamic sub navigation component */
type SubNavLinkProps = {
  href: string
  activeLink: string
  title: string
}
const SubNavLink: FCWC<SubNavLinkProps> = ({ activeLink, ...props }) => {
  const isActive = props.title === activeLink
  return (
    <Link
      type="base"
      target="_self"
      sx={(theme) => ({
        ...theme.typography["link.sm"],
        "color": theme.palette["neutral.700"].main,
        "position": "relative",
        "display": "inline-block",
        "textTransform": "capitalize",
        "py": 3,

        [theme.breakpoints.down(1400)]: {
          py: 2,
        },

        "&::before": {
          content: "''",
          bgcolor: theme.palette["primary.800"].main,
          position: "absolute",
          height: "1px",
          width: "100%",
          bottom: 0,
          transform: `scaleX(${isActive ? "1" : "0"})`,
          transformOrigin: "bottom left",
          transition: theme.transitions.create("transform"),
        },

        "&:hover::before": {
          transform: "scaleX(1)",
        },
      })}
      {...props}
    />
  )
}

/** A sub-component for the list of desktop links in the dynamic sub navigation component */
type SubNavDesktopLinksProps = Pick<
  DynamicSubNavProps,
  "links" | "travelInformation" | "tripIdea" | "activeLink" | "countryId"
> & {
  isTripPage?: boolean
}
const SubNavDesktopLinks: FC<SubNavDesktopLinksProps> = ({
  links,
  travelInformation,
  tripIdea,
  countryId,
  activeLink,
  isTripPage = false,
}) => {
  const isSticky = useContext(SubNavStickyContext)
  const showTravelInformation = Array.isArray(travelInformation) && travelInformation.length > 0

  return (
    <Stack
      direction="row"
      mx="auto"
      columnGap={showTravelInformation ? 15 : 20}
      sx={(theme) => ({
        marginRight: isSticky ? 3 : "auto",
        [theme.breakpoints.down(1400)]: {
          display: "none",
        },
      })}
    >
      {isTripPage ? (
        <>
          {links?.map((link, i) => (
            <Box
              key={i}
              py={5}
              minHeight={78}
              sx={(theme) => ({
                [theme.breakpoints.down(1400)]: isSticky && {
                  py: 4,
                },
              })}
            >
              <SubNavLink href={link.uri} activeLink={activeLink!} title={link.title}>
                {link.title}
              </SubNavLink>
            </Box>
          ))}
        </>
      ) : (
        <>
          {links?.slice(0, 1)?.map((link, i) => (
            <Box
              key={i}
              py={5}
              minHeight={78}
              sx={(theme) => ({
                [theme.breakpoints.down(1400)]: isSticky && {
                  py: 4,
                },
              })}
            >
              <SubNavLink href={link.uri} activeLink={activeLink!} title={link.title}>
                {link.title}
              </SubNavLink>
            </Box>
          ))}
          <Box
            py={5}
            display="flex"
            alignItems="center"
            minHeight={78}
            sx={(theme) => ({
              [theme.breakpoints.down(1400)]: isSticky && {
                py: 4,
              },
            })}
          >
            <GeoPages
              title="Vacation Packages"
              links={tripIdea!}
              countryId={countryId}
              activeLink={activeLink}
              defaultLink={{
                title: "All Vacation Packages",
                uri: links?.find((link) => link.title === "Trip Itineraries")?.uri || "",
              }}
            />
          </Box>
          {links?.slice(2, 3)?.map((link, i) => (
            <Box
              key={i}
              py={5}
              minHeight={78}
              sx={(theme) => ({
                [theme.breakpoints.down(1400)]: isSticky && {
                  py: 4,
                },
              })}
            >
              <SubNavLink href={link.uri} activeLink={activeLink!} title={link.title}>
                {link.title}
              </SubNavLink>
            </Box>
          ))}
          {showTravelInformation && (
            <Box
              py={5}
              display="flex"
              alignItems="center"
              minHeight={78}
              sx={(theme) => ({
                [theme.breakpoints.down(1400)]: isSticky && {
                  py: 4,
                },
              })}
            >
              <GeoPages
                title="Travel Information"
                links={travelInformation}
                activeLink={activeLink}
              />
            </Box>
          )}
          {links?.slice(3)?.map((link, i) => (
            <Box
              key={i}
              py={5}
              minHeight={78}
              sx={(theme) => ({
                [theme.breakpoints.down(1400)]: isSticky && {
                  py: 4,
                },
              })}
            >
              <SubNavLink href={link.uri} activeLink={activeLink!} title={link.title}>
                {link.title}
              </SubNavLink>
            </Box>
          ))}
        </>
      )}
    </Stack>
  )
}

/** A sub-component for the list of mobile links in the dynamic sub navigation component */
type SubNavMobileLinksProps = Pick<
  DynamicSubNavProps,
  "links" | "travelInformation" | "tripIdea" | "activeLink" | "countryId"
> & {
  isTripPage?: boolean
}
const SubNavMobileLinks: FC<SubNavMobileLinksProps> = ({
  links,
  travelInformation,
  tripIdea,
  countryId,
  activeLink,
  isTripPage = false,
}) => {
  const isSticky = useContext(SubNavStickyContext)
  const showTravelInformation = Array.isArray(travelInformation) && travelInformation.length > 0

  return (
    <Box
      flexBasis="100%"
      px={5}
      sx={(theme) => ({
        borderTop: `1px solid ${isSticky ? theme.palette["neutral.200"].main : "transparent"}`,
        overflow: isSticky ? "hidden" : "unset",
        [theme.breakpoints.up(1400)]: {
          display: "none",
        },
      })}
    >
      <Box
        sx={(theme) => ({
          ...(isSticky && {
            "overflowX": "auto",
            "scrollbarWidth": "none",
            "transition": theme.transitions.create("all"),
            "msOverflowStyle": "none",

            "&::-webkit-scrollbar": {
              display: "none",
            },
          }),
        })}
      >
        <Stack
          justifyContent={{ xs: "flex-start", sm: "center" }}
          direction="row"
          columnGap={{ xs: 9, md: 20 }}
          sx={{
            // target last child
            "& > :last-child": {
              paddingRight: isSticky ? 0 : 7,
            },
          }}
        >
          {isTripPage ? (
            <>
              {links?.map((link, i) => (
                <Stack
                  justifyContent="center"
                  minHeight={{ xs: 58, md: 74 }}
                  flex="0 0 auto"
                  key={i}
                  textAlign="center"
                >
                  <SubNavLink href={link.uri} activeLink={activeLink!} title={link.title}>
                    {link.title} {link.active}
                  </SubNavLink>
                </Stack>
              ))}
            </>
          ) : (
            <>
              {links?.slice(0, 1)?.map((link, i) => (
                <Stack
                  justifyContent="center"
                  minHeight={{ xs: 58, md: 74 }}
                  flex="0 0 auto"
                  key={i}
                  textAlign="center"
                >
                  <SubNavLink href={link.uri} activeLink={activeLink!} title={link.title}>
                    {link.title} {link.active}
                  </SubNavLink>
                </Stack>
              ))}
              <Box
                py={5}
                display="flex"
                alignItems="center"
                minHeight={78}
                sx={(theme) => ({
                  [theme.breakpoints.down(1400)]: isSticky && {
                    py: 4,
                  },
                })}
              >
                <GeoPages
                  title="Vacation Packages"
                  links={tripIdea!}
                  countryId={countryId}
                  activeLink={activeLink}
                  defaultLink={{
                    title: "All Vacation Packages",
                    uri:
                      links?.find((link) => link.title === "Trip Itineraries")?.uri ||
                      "/vacation-packages",
                  }}
                />
              </Box>
              {links?.slice(2, 3)?.map((link, i) => (
                <Stack
                  justifyContent="center"
                  minHeight={{ xs: 58, md: 74 }}
                  flex="0 0 auto"
                  key={i}
                  textAlign="center"
                >
                  <SubNavLink href={link.uri} activeLink={activeLink!} title={link.title}>
                    {link.title === "Trip Itineraries" ? "Vacation Packages" : link.title}
                  </SubNavLink>
                </Stack>
              ))}
              {showTravelInformation && (
                <Stack
                  justifyContent="center"
                  minHeight={{ xs: 58, md: 74 }}
                  flex="0 0 auto"
                  textAlign="center"
                >
                  <GeoPages
                    title="Travel Information"
                    links={travelInformation}
                    activeLink={activeLink}
                  />
                </Stack>
              )}
              {links?.slice(3)?.map((link, i) => (
                <Stack
                  justifyContent="center"
                  minHeight={{ xs: 58, md: 74 }}
                  flex="0 0 auto"
                  key={i}
                  textAlign="center"
                >
                  <SubNavLink href={link.uri} activeLink={activeLink!} title={link.title}>
                    {link.title} {link.active}
                  </SubNavLink>
                </Stack>
              ))}
            </>
          )}
        </Stack>
      </Box>
    </Box>
  )
}

/** A sub-component for the right content in the dynamic sub navigation component */
type SubNavRightContentProps = Pick<DynamicSubNavProps, "hideRight" | "trip">
const SubNavRightContent: FC<SubNavRightContentProps> = ({ hideRight = false, trip = null }) => {
  const isSticky = useContext(SubNavStickyContext)
  const [openInquiryModal, setOpenInquiryModal] = useState(false)
  const [openTripModal, setOpenTripModal] = useState(false)

  const siteMetadata = useContext(SiteMetadataContext)

  return (
    <>
      <Fade in={isSticky} unmountOnExit>
        <Box flex="1">
          {!hideRight && (
            <Stack
              direction="row"
              p={2}
              pr={3.5}
              columnGap={{ xs: 4, md: 5 }}
              alignItems="center"
              justifyContent="flex-end"
            >
              <Typography variant="body.xs" sx={{ lineHeight: 1, opacity: 0.7 }}>
                Call a Specialist
              </Typography>
              <Link
                type="base"
                variant="body.sm"
                href={siteMetadata.phoneUrl}
                sx={{
                  "fontWeight": 500,
                  "lineHeight": 1,
                  "background": (theme) =>
                    `linear-gradient(to right, ${theme.palette["primary.800"].main}, ${theme.palette["primary.800"].main})`,
                  "backgroundSize": "0 1px",
                  "backgroundPosition": "0 100%",
                  "backgroundRepeat": "no-repeat",
                  "pb": 0.5,
                  "transition": "background-size 400ms",

                  "&:hover": {
                    backgroundSize: "100% 1px",
                  },
                }}
              >
                {siteMetadata.phone}
              </Link>
              <Button
                variant="quaternary"
                sx={{
                  display: { xs: "none", md: "inline-block" },
                  padding: { xs: "10px 16px" },
                  minHeight: { xs: 38, md: 48 },
                }}
                onClick={() => {
                  if (trip) {
                    setOpenTripModal(true)
                  } else {
                    setOpenInquiryModal(true)
                  }
                }}
              >
                {"Request a Quote"}
              </Button>
            </Stack>
          )}
        </Box>
      </Fade>
      <InquiryModal open={openInquiryModal} onClose={() => setOpenInquiryModal(false)} />
      {trip && (
        <TripModal
          open={openTripModal}
          onClose={() => setOpenTripModal(false)}
          trip={trip as TripData}
        />
      )}
    </>
  )
}

// A sub-component for the travel information dropdown
type GeoPageProps = {
  links: GeoPageLink[]
  title: string
  defaultLink?: { title: string; uri: string } | null
  countryId?: number | null
  activeLink?: string | null
}
const GeoPages = ({
  links: unsortedLinks,
  title,
  defaultLink = null,
  countryId = null,
  activeLink = null,
}: GeoPageProps) => {
  const popperRef = useRef<HTMLDivElement>()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const isActive = activeLink ? activeLink === title : false
  const links = unsortedLinks?.length
    ? [...unsortedLinks].sort((a, b) => a.title.localeCompare(b.title))
    : []

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
    setIsOpen(!isOpen)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (
      popperRef.current &&
      !popperRef.current.contains(event.target as Node) &&
      anchorEl &&
      !anchorEl.contains(event.target as Node)
    ) {
      setAnchorEl(null)
      setIsOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [anchorEl])

  const id = isOpen ? "simple-popper" : undefined

  return (
    <Box
      sx={{
        display: "inline-block",
        position: "relative",
      }}
    >
      <ButtonBase
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: theme.spacing(0.5),
          minHeight: "34px",
        })}
        aria-describedby={id}
        aria-controls={isOpen ? "dropdown-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={Boolean(isOpen) ? "true" : "false"}
        onClick={handleClick}
      >
        <Typography
          variant="link.sm"
          color={(theme) => theme.palette["neutral.700"].main}
          sx={{
            whiteSpace: "nowrap",
            position: "relative",
            ...(isActive && {
              "&::before": {
                content: "''",
                bgcolor: (theme) => theme.palette["primary.800"].main,
                position: "absolute",
                height: "1px",
                width: "100%",
                bottom: "-12px",
                transform: `scaleX(1)`,
                transformOrigin: "bottom left",
                transition: (theme) => theme.transitions.create("transform"),
              },
            }),
          }}
        >
          {title}
        </Typography>
        <SvgIcon
          variant="dropdownIcon"
          viewBox="0 0 16 16"
          size={18}
          sx={(theme) => ({
            fill: theme.palette["neutral.700"].main,
            transition: "transform 0.2s",
            transform: `rotate(${Boolean(isOpen) ? "0deg" : "180deg"})`,
            zIndex: 1,
          })}
        />
      </ButtonBase>
      <Popper
        id={id}
        open={isOpen}
        anchorEl={anchorEl}
        placement="bottom"
        sx={{ zIndex: 1, maxWidth: { xs: 200, md: 243 } }}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 25], // [horizontal, vertical] offset
            },
          },
        ]}
      >
        <Box
          ref={popperRef}
          sx={(theme) => ({
            borderRadius: "4px",
            background: theme.palette["neutral.100"].main,
            boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.10)",
            minWidth: { xs: "auto", md: 243 },
            maxHeight: 266,
            overflowY: "auto",
            padding: "20px 20px 16px 20px",
          })}
        >
          {defaultLink && (
            <Link type="base" target="_self" href={defaultLink.uri}>
              <ButtonBase
                onClick={handleClick}
                sx={(theme) => ({
                  "padding": theme.spacing(4, 3),
                  "borderRadius": theme.spacing(1),
                  "transition": theme.transitions.create(["background", "color"]),
                  "width": "100%",
                  "justifyContent": "flex-start",
                  "&:hover": {
                    "background": theme.palette["secondary.200"].main,
                    "& span": {
                      color: theme.palette["secondary.800"].main,
                    },
                  },
                })}
              >
                <Typography variant="link.sm" color={(theme) => theme.palette["neutral.700"].main}>
                  {defaultLink.title}
                </Typography>
              </ButtonBase>
            </Link>
          )}
          {links?.map((item, index) => (
            <Link key={item?.title} type="base" target="_self" href={item?.uri}>
              <ButtonBase
                key={item?.title}
                onClick={handleClick}
                sx={(theme) => ({
                  "padding": theme.spacing(4, 3),
                  "borderRadius": theme.spacing(1),
                  "transition": theme.transitions.create(["background", "color"]),
                  "width": "100%",
                  "justifyContent": "flex-start",
                  "&:hover": {
                    "background": theme.palette["secondary.200"].main,
                    "& span": {
                      color: theme.palette["secondary.800"].main,
                    },
                  },
                })}
              >
                <Typography
                  variant="link.sm"
                  color={(theme) => theme.palette["neutral.700"].main}
                  sx={{ textAlign: "left" }}
                >
                  {item?.title}
                </Typography>
              </ButtonBase>
            </Link>
          ))}
        </Box>
      </Popper>
    </Box>
  )
}
